import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, User, Home } from 'lucide-react';

// Sample blog articles data with images (static articles)
const staticBlogArticles = [
  {
    id: 'weak-access-management-risks',
    title: 'The Hidden Risks of Weak Access Management',
    excerpt: 'Weak access management leads to unauthorized users gaining access to confidential data. Learn how IAM strengthens security and streamlines compliance.',
    content: `
# The Hidden Risks of Weak Access Management

Weak access management leads to unauthorized users gaining access to confidential business data, which increases the risk of breaches and regulatory penalties.

## Why IAM is Essential

Adopting Identity and Access Management (IAM) ensures only the right people can reach sensitive resources, strengthens cybersecurity with automation and multi-factor authentication, and streamlines compliance and onboarding.

### Key Benefits:
- **Strengthened Security**: Automation and MFA reduce human error.
- **Streamlined Compliance**: Easier to prove who has access to what.
- **Efficient Onboarding**: Get new team members productive faster.

## Take Action Today

Review your organization’s current access controls and implement a robust IAM solution. 

For tailored guidance, book an obligation-free consultation today.
    `,
    author: 'IDNTY1 Team',
    date: '2026-02-26',
    image: 'https://media.licdn.com/dms/image/v2/D5622AQFEm-lQ45GwqQ/feedshare-shrink_2048_1536/B56ZrCr17aHQA0-/0/1764202867780?e=1773878400&v=beta&t=nZjT3zMPcuyqE4VeKIV6unCuxSJ8F-mTSakb_4g4Q9w',
    category: 'Security',
    readTime: '3 min read',
    tags: ['IAM', 'Data Security', 'Compliance', 'Business Efficiency']
  },
  {
    id: 'orphaned-privileged-accounts-risk',
    title: 'Unsure how many orphaned privileged accounts exist in your organization?',
    excerpt: '70% of organizations fail to discover all privileged accounts. Learn how Just-in-Time (JIT) access can reduce your attack surface.',
    content: `
# Unsure how many orphaned privileged accounts exist in your organization?

According to a recent survey, 70% of organizations failed to discover all privileged accounts, and about 55% reported they do not consistently revoke privileged access after employees leave.

## The Problem: Persistent Privileges

Persistent privileges and orphaned accounts introduce major security risk. Over time, users accumulate excessive permissions, service or contractor accounts linger, and IT often lacks clear visibility into who has elevated access.

## The Solution: Just-in-Time (JIT) Access

A mature IAM / PAM strategy using Just-in-Time (JIT) access helps. Rather than granting permanent administrator rights, users request temporary elevated access when needed, with automation revoking it once the task completes. 

### Benefits of JIT:
- Reduces standing privileges
- Improves auditability
- Tightens the attack surface for both human and machine identities

## Building a Modern IAM Posture

Leading platforms combine JIT access with automated lifecycle management, ensuring that access grants are time-bound, approved, and regularly reviewed. This helps minimize forgotten service accounts and ghost credentials.

Ready to strengthen your IAM with JIT and reduce orphaned privileges? Let’s talk about transforming your identity security.
    `,
    author: 'IDNTY1 Team',
    date: '2026-02-26',
    image: 'https://media.licdn.com/dms/image/v2/D5622AQFaCNKGIC2JWg/feedshare-shrink_800/B56ZrHRbGpI0Ag-/0/1764279960446?e=1773878400&v=beta&t=akqHVGO88FDbP2c38_ECXg48NSl4UfgQ9BEzXugmEmc',
    category: 'Security',
    readTime: '4 min read',
    tags: ['IAM', 'JustInTime', 'PAM', 'Zero Trust']
  },
  {
    id: 'iam-success-pillars',
    title: 'Do you know what IAM success truly looks like?',
    excerpt: 'IAM success is measurable. Discover the four pillars every organization should focus on to deliver real security and efficiency.',
    content: `
# Do you know what IAM success truly looks like?

Here are four pillars every organization should focus on with measurable targets:

## 1. Access and Security
- Strong authentication with success rate above 95 percent.
- Full adoption of multi factor authentication reaching 100 percent.
- Rapid onboarding and removal of accounts within 24 hours.
- Zero unused or overprivileged identities after quarterly reviews.

## 2. Governance and Compliance
- Access reviewed regularly with over 95 percent completion rate.
- Continuous regulatory alignment maintaining 100 percent compliance.
- No unapproved overrides keeping authorization failures under 5 percent.

## 3. Operations and Experience
- Sign in is user friendly with lockout rates below 2 percent.
- Centralized reporting on all access events via SIEM integration.
- Incidents detected and addressed with mean time to detect under 24 hours.
- Data drives real improvement through monthly metric reviews.

## 4. Monitoring and Visibility
- Real time threat detection with behavioral analytics active.
- Anomaly detection identifying suspicious patterns within minutes.
- Audit logging capturing 100 percent of access events.
- Security incident response time under industry benchmarks.

With these criteria and metrics, IAM delivers real security, compliance, and efficiency while keeping friction for users low.
    `,
    author: 'IDNTY1 Team',
    date: '2026-02-26',
    image: 'https://media.licdn.com/dms/image/v2/D5622AQHeXzKP12CbJQ/feedshare-shrink_800/B56ZrcIH4iL0Ag-/0/1764629710320?e=1773878400&v=beta&t=YMD1POzm4SRVZHR5DWjtjILhZgXzpUqYGuwuptQJh9Q',
    category: 'Best Practices',
    readTime: '5 min read',
    tags: ['IAM', 'Security Metrics', 'Compliance', 'Zero Trust']
  },
  {
    id: 'visibility-on-service-accounts',
    title: 'Do you have visibility on the service accounts used by your apps?',
    excerpt: 'Until recently many service accounts were created directly in each application. Learn why centralizing service account management is critical for security.',
    content: `
# Do you have visibility on the service accounts used by your apps?

Until recently many service accounts were created directly in each application and not in the identity platform. This meant there was no central record, no clear owner, and no easy way to remove or update them when projects ended, which increased security risk.

Now more teams create and manage service accounts in the identity platform first and then link them to each application. This gives one place to see every service identity, control who can create them, and apply the same lifecycle rules as human users.

## Key Benefits of Centralized Service Account Management:

- **Clear View**: One place to see all service accounts and where they are used.
- **Easier Audits**: Access is visible in one system, making reviews and compliance much simpler.
- **Better Security**: Centralized credential rotation and fewer "forgotten" accounts that could be exploited.

## Modern IAM for Service Identities

Managing service accounts shouldn't be an afterthought. By applying the same rigor to service identities as you do to human users, you significantly reduce your organization's attack surface.
    `,
    author: 'IDNTY1 Team',
    date: '2026-02-26',
    image: 'https://media.licdn.com/dms/image/v2/D5622AQEyRm9etj8FAg/feedshare-shrink_800/B56Zr7n3_xGkAs-/0/1765158129327?e=1773878400&v=beta&t=2JAZGXUwXwq-F4pkuaRBgFpi5XqegOutL49iz101v0E',
    category: 'Security',
    readTime: '3 min read',
    tags: ['Service Accounts', 'IAM', 'Cloud Security', 'Identity Management']
  },
  {
    id: 'is-your-organization-growing',
    title: 'Is your organization growing?',
    excerpt: 'For many growing teams, growth leads to more helpdesk tickets, slower onboarding, and unclear access. Learn how to manage access effectively.',
    content: `
# Is your organization growing?

For many growing teams, this leads to:
- More helpdesk tickets
- Slower onboarding
- Unclear access when people change roles or leave

Access management starts to matter at this stage. Not only for security, but for daily operations as well.

When access is handled well:
- New hires get productive faster
- Teams face fewer interruptions
- Risk stays visible and manageable

## Free Review of Your Current Setup

If you already have an IAM or SSO system in place, we offer a free review of your current setup.

[Start a conversation with us](https://www.ssolabs.io)

It is a simple way to understand what is working today and where small improvements can make a big difference.
    `,
    author: 'IDNTY1 Team',
    date: '2026-02-26',
    image: 'https://media.licdn.com/dms/image/v2/D5622AQG_6BLLGJkDZg/feedshare-shrink_800/B56ZuW6.YZIcAg-/0/1767763602297?e=1773878400&v=beta&t=q5-l9Ae7fKvuZ8F5cfV5FSHbjvp64WIzTZT3YZiMuMY',
    category: 'Operations',
    readTime: '3 min read',
    tags: ['Growing Teams', 'Operations', 'Access Management', 'Business Growth']
  },
  {
    id: 'is-your-iam-actually-working',
    title: 'You have IAM. But is it actually working for you?',
    excerpt: 'Most organizations are somewhere on the maturity journey—from managing exceptions manually to having IAM that truly enables the business.',
    content: `
# You have IAM. But is it actually working for you?

Most organizations are somewhere on the maturity journey—from managing exceptions manually to having IAM that truly enables the business.

Where does your organization stand?

## The IAM Maturity Journey

Managing digital identities and access is not a one-time project, but a continuous journey of improvement. Many businesses start with manual processes, which are prone to error and security gaps. As they mature, they move towards automated, policy-driven, and eventually, business-enabling IAM solutions.

### Stage 1: Manual & Reactive
- Access is granted on request
- No formal review process
- High risk of "privilege creep"

### Stage 2: Standardized
- Basic roles are defined
- Some automated provisioning
- Periodic access reviews

### Stage 3: Optimized & Enabling
- Zero Trust principles applied
- Just-in-time access
- IAM as a business accelerator

## Get a complimentary IAM maturity assessment

Ready to find out where you stand and how to reach the next level?

[Get your assessment here](https://idnty.one/contact)
    `,
    author: 'IDNTY1 Team',
    date: '2026-02-26',
    image: 'https://media.licdn.com/dms/image/v2/D5622AQFhI3EGAf1oIg/feedshare-shrink_800/B56Zuak_pRG4Ag-/0/1767824949316?e=1773878400&v=beta&t=O4OjCpR3HoI1B4m8Jt4sTWlcATqXTP_66-Rno7E6xC8',
    category: 'Security',
    readTime: '3 min read',
    tags: ['IAM', 'Maturity Assessment', 'Identity Management', 'Business Security']
  },
  {
    id: 'why-your-business-needs-better-access-control',
    title: 'Why your business needs better access control?',
    excerpt: 'Most businesses operate without realizing they are sitting on a major security risk. Learn why access control is critical for your business.',
    content: `
# Why your business needs better access control?

Most businesses operate without realizing they are sitting on a major security risk.

Here is what typically happens: 
1. An employee leaves, but their login still works. 
2. A contractor finishes a project, yet they still have access to your files. 
3. Your accountant can see customer data they do not need. 
4. Multiple people share one admin password for your cloud software. No one knows who approved access or when it was last reviewed.

This is not just messy. It creates real exposure. A disgruntled former employee could delete files. An unmonitored account could be used for fraud. A data leak could happen simply because too many people had access to sensitive information.

And the worst part? You would not even know until the damage is done.

## What is Identity and Access Management?

IAM is simply about controlling who gets into your systems and what they can do once inside. It ensures the right people have the right access, nothing more. Think of it as digital keys to your business. You would not give every employee a master key to your office. The same logic applies online.

## Three simple steps to start today:
1. Secure critical accounts with two-step login for email, banking, and cloud tools.
2. Review and remove access for former employees, contractors, or anyone no longer working with you.
3. Assign one person to approve and monitor who gets access to sensitive systems and data.

## Why This Matters:

Good access control prevents mistakes, protects your reputation, and gives you full visibility into who holds the keys to your business. You do not need a big IT team to get started.

Small businesses are not too small to be targeted. But they can be too prepared to be victims.

## Ready to secure your business the right way?

We help small and medium businesses set up simple, effective identity and access management without the tech overwhelm. [Contact us](https://lnkd.in/gJaDsbEf) and let us walk you through it.
    `,
    author: 'IDNTY1 Team',
    date: '2026-02-26',
    image: 'https://media.licdn.com/dms/image/v2/D5622AQEVMsO5jUGn6w/feedshare-shrink_800/B56ZvT48dMHsAg-/0/1768786479270?e=1773878400&v=beta&t=42ErCqig_qtAiNWFjWka5_MtnKbu5t0OLJqpBx0G8w4',
    category: 'Security',
    readTime: '4 min read',
    tags: ['Identity Management', 'Access Control', 'SMB Security', 'Cyber Security']
  },
  {
    id: 'identity-management-access-control-smb',
    title: 'Identity Management & Access Control: A Complete Guide for SMBs',
    excerpt: 'Learn how small and medium businesses can implement robust identity management and access control solutions without breaking the budget.',
    content: `
# Identity Management & Access Control: A Complete Guide for SMBs

In today's digital landscape, small and medium businesses (SMBs) face the same cybersecurity threats as large enterprises, but often with limited resources. Identity management and access control have become critical components of any business's security strategy.

## Why Identity Management Matters

Every business today manages digital identities - employees, contractors, partners, and customers. Without proper identity management:

- Unauthorized users can access sensitive data
- Compliance violations can result in hefty fines
- Security breaches can damage reputation and trust

## The Core Components of Identity Management

### 1. Identity Verification
Ensuring that users are who they claim to be through multi-factor authentication (MFA), single sign-on (SSO), and biometric verification.

### 2. Access Control
Defining what resources each identity can access and what actions they can perform. This includes:
- Role-based access control (RBAC)
- Attribute-based access control (ABAC)
- Principle of least privilege

### 3. Lifecycle Management
Managing identities from onboarding through offboarding, including:
- Automated provisioning and deprovisioning
- Periodic access reviews
- Credential management

## Best Practices for SMBs

1. **Start with a Strong Foundation**: Implement MFA for all users, especially for admin accounts.

2. **Automate Where Possible**: Use identity management tools that automate repetitive tasks.

3. **Regular Audits**: Conduct quarterly access reviews to ensure appropriate access levels.

4. **Employee Training**: Educate staff on security best practices and phishing prevention.

5. **Zero Trust Architecture**: Never trust, always verify - assume breach mentality.

## Conclusion

Identity management does not have to be complicated or expensive. With the right tools and approach, SMBs can implement enterprise-grade security that protects their assets while enabling business growth.

Ready to strengthen your identity security? Learn more about IDNTY1's identity management solutions.
    `,
    author: 'IDNTY1 Team',
    date: '2026-02-26',
    image: 'https://images.unsplash.com/photo-1555949963-aa784d420c4e?w=400&h=300&fit=crop',
    category: 'Security',
    readTime: '5 min read',
    tags: ['Identity Management', 'Access Control', 'SMB Security', 'Zero Trust']
  },
  {
    id: 'smb-security-best-practices',
    title: 'Essential SMB Security Best Practices for 2026',
    excerpt: 'Discover the top security practices that every small and medium business should implement to protect against modern threats.',
    content: `
# Essential SMB Security Best Practices for 2026

As cyber threats continue to evolve, small and medium businesses must stay vigilant. Here are the essential security practices for 2026.

## Top Threats Facing SMBs

- Ransomware attacks
- Phishing and social engineering
- Insider threats
- Supply chain attacks
- Cloud misconfigurations

## Key Security Practices

### 1. Implement Multi-Factor Authentication
MFA should be mandatory for all accounts, especially:
- Email accounts
- Financial systems
- Admin dashboards
- VPN access

### 2. Regular Security Training
Your employees are your first line of defense:
- Conduct monthly security awareness training
- Run simulated phishing exercises
- Keep staff updated on latest threats

### 3. Backup Everything
Follow the 3-2-1 backup rule:
- 3 copies of data
- 2 different storage types
- 1 offsite backup

### 4. Keep Systems Updated
Regularly patch and update:
- Operating systems
- Applications
- Firmware
- Dependencies

### 5. Monitor External Threats
Use external threat monitoring to:
- Detect compromised credentials
- Identify brand impersonation
- Monitor for data leaks

## Conclusion

Security is an ongoing process, not a one-time setup. Stay proactive and protect your business.
    `,
    author: 'IDNTY1 Team',
    date: '2026-02-20',
    image: 'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=400&h=300&fit=crop',
    category: 'Best Practices',
    readTime: '4 min read',
    tags: ['Security', 'Best Practices', 'SMB', 'Cyber Defense']
  },
  {
    id: 'zero-trust-security-explained',
    title: 'Zero Trust Security: What Every Business Needs to Know',
    excerpt: 'Zero trust is more than a buzzword - it is a fundamental shift in how we think about security. Learn what it means and how to implement it.',
    content: `
# Zero Trust Security: What Every Business Needs to Know

Zero Trust has become one of the most talked-about concepts in cybersecurity. But what does it really mean, and how can SMBs implement it?

## The Zero Trust Philosophy

Traditional security models operate on a "castle and moat" approach - once inside the network, users are trusted. Zero Trust flips this on its head:

**Never trust, always verify.**

### Core Principles

1. **Verify Explicitly**: Always authenticate and authorize based on all available data points.

2. **Use Least Privilege Access**: Limit user access with Just-In-Time and Just-Enough-Access.

3. **Assume Breach**: Minimize blast radius and segment access to prevent lateral movement.

## Implementing Zero Trust

### Start with Identity
- Implement strong authentication
- Use multi-factor authentication
- Deploy single sign-on where appropriate

### Secure Your Endpoints
- Endpoint detection and response (EDR)
- Regular patching
- Device compliance checks

### Monitor Everything
- Continuous validation
- Real-time threat detection
- Behavioral analytics

## Benefits for SMBs

- Better protection against breaches
- Improved compliance posture
- Reduced attack surface
- Enhanced visibility

## Conclusion

Zero Trust is not just for enterprises. SMBs can and should implement zero trust principles to protect their assets.
    `,
    author: 'IDNTY1 Team',
    date: '2026-02-15',
    image: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=400&h=300&fit=crop',
    category: 'Security',
    readTime: '6 min read',
    tags: ['Zero Trust', 'Security', 'Enterprise', 'Cybersecurity']
  }
];

const Blog = () => {
   const [filterCategory, setFilterCategory] = useState('');
   const [blogArticles, setBlogArticles] = useState(staticBlogArticles);
   const [loading, setLoading] = useState(true);

  const categories = [...new Set(blogArticles.map(article => article.category))];

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        // Assuming the API is served from the same domain, or adjust URL as needed
        const apiUrl = process.env.REACT_APP_API_URL || '/api/blog-posts';
        const response = await fetch(apiUrl);
        if (response.ok) {
          const data = await response.json();
          // Combine static articles with API posts
          const apiArticles = data.posts.map(post => ({
            id: post.id,
            title: post.title,
            excerpt: post.excerpt,
            content: post.content,
            author: post.author,
            date: post.published_at,
            image: post.image_url || 'https://www.idnty.one/fNew_logo_2.png',
            category: 'Company Update',
            readTime: '3 min read',
            tags: post.tags || ['linkedin'],
            linkedin_url: post.linkedin_url
          }));
          const allArticles = [...staticBlogArticles, ...apiArticles].sort((a, b) => {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            return dateB - dateA; // descending
          });
          setBlogArticles(allArticles);
        }
      } catch (error) {
        console.error('Failed to fetch blog posts:', error);
        // Fall back to static articles
        setBlogArticles(staticBlogArticles);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const [filteredArticles, setFilteredArticles] = useState(blogArticles);

  useEffect(() => {
    setFilteredArticles(blogArticles);
  }, [blogArticles]);

   const handleFilter = (category) => {
     setFilterCategory(category);
   };

   return (
    <div className="min-h-screen bg-[#f9fafb]">
       {/* Header with Home Link */}
       <header className="bg-white border-b border-gray-200 py-4">
         <div className="container mx-auto px-4">
           <a 
             href="https://www.idnty.one" 
             className="inline-flex items-center text-sm text-gray-600 hover:text-[#2563eb] transition-colors"
           >
             <Home className="w-4 h-4 mr-2" />
             Back to IDNTY1
           </a>
         </div>
       </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#eff6ff] to-[#f9fafb] py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xl text-gray-600 mb-8">
              Insights, guides, and best practices for identity security and access management
            </p>
            
            <div className="flex flex-wrap justify-center gap-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => handleFilter(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium ${
                    filterCategory === category
                      ? 'bg-[#2563eb] text-white'
                      : 'bg-[#eff6ff] text-[#2563eb]'
                  } transition-colors hover:bg-[#dbeafe]`}
                >
                  {category}
                </button>
              ))}
              <button
                onClick={() => handleFilter('All')}
                className={`px-4 py-2 rounded-full text-sm font-medium ${
                  filterCategory === 'All' || !filterCategory
                    ? 'bg-[#2563eb] text-white'
                    : 'bg-[#eff6ff] text-[#2563eb]'
                } transition-colors hover:bg-[#dbeafe]`}
              >
                All
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Articles Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2563eb]"></div>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {filteredArticles.map((article) => (
              <article 
                key={article.id} 
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col"
              >
                {/* Article Image */}
                <div className="h-48 sm:h-64 overflow-hidden bg-gray-100">
                  <img 
                    src={article.image || 'https://www.idnty.one/fNew_logo_2.png'} 
                    alt={article.title}
                    className={`w-full h-full ${article.image ? 'object-cover hover:scale-105' : 'object-contain p-4'} transition-transform duration-300`}
                    loading="lazy"
                  />
                </div>

                {/* Article Content */}
                <div className="p-6 flex-1 flex flex-col">
                  {/* Category & Read Time */}
                  <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                    <span className="bg-[#eff6ff] text-[#2563eb] px-3 py-1 rounded-full text-xs font-medium">
                      {article.category}
                    </span>
                    <span>{article.readTime}</span>
                  </div>

                  {/* Title */}
                  <h2 className="text-xl font-semibold mb-3 line-clamp-2 text-gray-900">
                    <Link
                      to={`/blog/${article.id}`}
                      className="hover:text-[#2563eb] transition-colors"
                    >
                      {article.title}
                    </Link>
                  </h2>

                  {/* Excerpt */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-1">
                    {article.excerpt}
                  </p>

                   {/* Footer */}
                   <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-4 border-t border-gray-100">
                     <div className="flex items-center gap-2 text-sm text-gray-500">
                       <User className="w-4 h-4" />
                       <span>{article.author}</span>
                     </div>
                     <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                       <Link
                         to={`/blog/${article.id}`}
                         className="mt-2 sm:mt-0 inline-flex items-center text-[#2563eb] font-medium hover:underline text-sm"
                       >
                         Read Article
                         <ArrowRight className="w-4 h-4 ml-1" />
                       </Link>
                       {article.linkedin_url && (
                         <a
                           href={article.linkedin_url}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="mt-2 sm:mt-0 inline-flex items-center text-gray-600 font-medium hover:underline text-sm"
                           title="View on LinkedIn"
                         >
                           LinkedIn
                           <ArrowRight className="w-4 h-4 ml-1" />
                         </a>
                       )}
                     </div>
                   </div>
                </div>
              </article>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* LinkedIn Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4 text-black">Stay Updated</h2>
            <p className="text-black mb-8">
              For more articles and insights on identity security, follow us on LinkedIn.
            </p>
            <a 
              href="https://www.linkedin.com/company/idnty1/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors shadow-md"
            >
              Follow us on LinkedIn
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-200 bg-white">
        <div className="container mx-auto px-4 text-center text-sm text-gray-500">
          <p>© 2026 IDNTY1. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Blog;
